<template>
  <div>
    <a-select
      :key="selectKey"
      style="width: 100%"
      v-model="form[keyCode]"
      :size="'large'"
      show-search
      @change="handleChange"
      @search="handleSearch"
      @focus="onItemFocus"
      @blur="onItemBlur"
      @popupScroll="handlePopupScroll"
      :disabled="isDisable"
      :filter-option="false"
    >
      <a-select-opt-group label="" v-if="localCountryList?.length > 0">
        <a-select-option
          v-for="country in localCountryList"
          :key="keyCode + country.code"
          :value="country.code"
          :title="country.name"
        >
          <template v-if="showEmoji"> {{ country.emoji }}</template>
          {{ country.name }}
        </a-select-option>
      </a-select-opt-group>
      <a-select-opt-group v-if="visibleOtherList.length > 0">
        <template #label v-if="visibleOtherList.length > 0 && localCountryList?.length > 0">
          <div class="horizontal-divider"></div>
        </template>
        <a-select-option v-for="item in visibleOtherList" :key="keyCode + item.code" :value="item.code" :title="item.name">
          <template v-if="showEmoji"> {{ item.emoji }}</template>
          {{ item.name }}
        </a-select-option>
      </a-select-opt-group>
    </a-select>
  </div>
</template>
<script>
import { isShowEmoji } from '@/views/checkout/tools'

// 首屏渲染的选项条数与每次滚动到底部追加的条数（250 个国家全量渲染会有明显的滚动卡顿）
const WINDOW_SIZE = 60
const WINDOW_STEP = 40
// 距底部小于这个距离（约 3 个选项高度）就触发追加
const SCROLL_THRESHOLD = 96

export default {
  components: {},
  props: {
    form: {
      type: Object,
      default: () => ({})
    },
    keyCode: {
      type: String,
      default: 'country'
    },
    onChange: {
      type: Function,
      default: null
    },
    onFocus: {
      type: Function,
      default: null
    },
    onBlur: {
      type: Function,
      default: null
    },
    isDisable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectKey: this.keyCode + new Date().getTime(),
      country: null,
      allDataList: [],
      localCountryList: [],
      localCountrySaveList: [],
      otherCountryList: [], // 当前下拉框待展示的全量数据（搜索时会被过滤）
      windowSize: WINDOW_SIZE, // 当前渲染窗口大小，滚动到底部时逐步放大
      windowLocked: false // 一次渲染周期内只允许追加一次，避免快速拖动滚动条时瞬间渲染全部
    }
  },
  computed: {
    // 每帧都可能重新渲染整份列表，isShowEmoji() 之前是在 v-for 里逐条调用的，改成 computed 只求值一次
    showEmoji() {
      return isShowEmoji()
    },
    // 真正渲染到 DOM 的选项，其余部分滚动到底部时再分批渲染（ant-design-vue 1.x 的 Select 没有虚拟滚动）
    visibleOtherList() {
      // 已选中项必须在渲染范围内：Select 的显示文案是从已渲染的 children 里做 value -> label 映射，
      // 选项没被渲染时选择框会退化成显示国家 code（vc-select 的 getOptionInfoBySingleValue 兜底逻辑）
      const selectedIndex = this.otherCountryList.findIndex((item) => item.code === this.form[this.keyCode])
      return this.otherCountryList.slice(0, Math.max(this.windowSize, selectedIndex + 1))
    }
  },
  mounted() {},
  methods: {
    onItemFocus() {
      if (this.onFocus) {
        this.onFocus()
      }
    },
    onItemBlur(value) {
      if (this.onBlur) {
        this.onBlur(this.keyCode, value, 'select')
      }
    },
    handleChange(value) {
      this.localCountryList = [...this.localCountrySaveList]
      this.otherCountryList = [...this.allDataList]
      this.resetWindow()
      if (this.onChange) {
        this.onChange(value)
      }
    },
    // 初始化成需要的数据结构，顺带把搜索用小写名预先算好，避免每次按键对 250 条重复 toLowerCase
    initList(list) {
      return (list || []).map((item) => ({
        name: item.name,
        code: item.code,
        emoji: item.emoji,
        searchText: String(item.name || '').toLowerCase()
      }))
    },
    resetWindow() {
      this.windowSize = WINDOW_SIZE
      this.windowLocked = false
    },
    // 1. 滚动到底部附近时放大渲染窗口，DOM 里始终只保留已加载的这部分选项
    handlePopupScroll(e) {
      if (this.windowLocked || this.visibleOtherList.length >= this.otherCountryList.length) {
        return
      }
      const { scrollTop, scrollHeight, clientHeight } = e.target
      // 距离底部不足三个选项高度时加载更多
      if (scrollTop + clientHeight > scrollHeight - SCROLL_THRESHOLD) {
        this.windowSize += WINDOW_STEP
        this.windowLocked = true
        this.$nextTick(() => {
          this.windowLocked = false
        })
      }
    },
    // 2. 处理搜索（即时过滤 + 重置渲染窗口）
    handleSearch(value) {
      this.resetWindow()
      if (!value) {
        this.localCountryList = [...this.localCountrySaveList]
        this.otherCountryList = [...this.allDataList]
        return
      }
      const keyword = String(value).toLowerCase()
      this.otherCountryList = this.allDataList.filter((item) => item.searchText.includes(keyword))
      this.localCountryList = this.localCountrySaveList.filter((item) => item.searchText.includes(keyword))
    },
    setAllData(allList = [], localList = []) {
      this.allDataList = this.initList(allList)
      this.localCountrySaveList = this.initList(localList)
      this.localCountryList = [...this.localCountrySaveList]
      this.otherCountryList = [...this.allDataList]
      this.resetWindow()
    }
  }
}
</script>
<style lang="less" scoped>
.horizontal-divider {
  height: 1.5px;
  display: block;
  width: 100%;
  margin: 10px 0;
  background: #e9e9e9;
}

.scroll-area {
  height: 200px;
}
</style>
